/**
 * WHICH LAYOUTS CAN CARRY A PROGRESS GATE, AND HOW ONE IS RESOLVED.
 *
 * This is the ONE home of both rules. The build reads it (`resolveBlockingSections`),
 * and the frontend reads a GENERATED copy — `apps/fe/src/blocking-rules.generated.js`,
 * produced by `packages/schema/scripts/derive-blocking-rules.mjs` and held in step by
 * `blocking-rules.generated.test.ts`. Same arrangement as
 * `brand-colour-fields.generated.js` (`feedback_one_rule_one_place`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ WHY ONLY SOME LAYOUTS. Omar, 2026-09-04:
 *
 *   "Some layouts are purely informational and do not require any learner
 *    interaction … there is nothing the learner needs to complete in order to
 *    unlock progression. Therefore they should not receive a Progress gate …
 *    Blocking one of these layouts would not make sense because the Player would
 *    have no completion event or learner action it could use to determine when
 *    the screen should become unlocked. Please first verify which existing layout
 *    types actually have a valid completion condition."
 *
 * VERIFIED, not assumed, by reading the pinned runtime (`dynamo-player/js/components.js`)
 * rather than reasoning from the authoring schema. The test is where
 * `enableContinueButton` is called for each type's init function in
 * `customFunctionalities`:
 *
 *   · at the function's OWN top level  → the gate opens the instant the screen draws.
 *                                        A gate here is decoration: it can never hold
 *                                        anyone, and the bar's "Complete the content
 *                                        above before moving on" is then a false
 *                                        instruction (`label_completeBeforeContinue`).
 *   · only inside an event handler     → a real completion condition exists.
 *
 * | layout type                 | where Continue is enabled                              | gate |
 * |-----------------------------|--------------------------------------------------------|------|
 * | `title`                     | first line of `title()`                     `:3096`    |  no  |
 * | `two_columns_text`          | first line of `two_columns_text()`          `:3101`    |  no  |
 * | `text_and_image`            | first line of `text_and_image()`            `:3106`    |  no  |
 * | `fullscreen_text_and_image` | first line of `fullscreen_text_and_image()` `:3126`    |  no  |
 * | `object_viewer`             | last line of `object_viewer()`, unconditional `:5246`  |  no  |
 * | `fullscreen_video`          | video `ended`                               `:3246`    | YES  |
 * | `small_video`               | video `ended`                               `:3246`    | YES  |
 * | `sequence`                  | last tab reached                            `:4238`    | YES  |
 * | `vertical_tabs`             | every tab visited                           `:4319`    | YES  |
 * | `horizontal_tabs`           | every tab visited                           `:4415`    | YES  |
 * | `icons_discover`            | every icon visited                          `:3925`    | YES  |
 * | `hidden_items`              | every item visited                          `:3849`    | YES  |
 * | `quiz_images`               | question answered / results reached  `:5064` `:5204`   | YES  |
 * | `quiz_gaming`               | emits `<type>quiz_images</type>` — same functionality   | YES  |
 *
 * ★ TWO RESULTS WORTH KNOWING, because neither is what the layout's NAME suggests.
 *
 * 1. **`object_viewer` is informational**, even though it has clickable hotspots and
 *    calls `_hidden_items_initEvents` to run them. Its `enableContinueButton` sits
 *    OUTSIDE every handler, at the end of its init, so the gate opens on render no
 *    matter what the learner does. Omar's list named four types; this is a fifth, and
 *    it is reported rather than folded in silently.
 * 2. **`text_and_image`'s inline videos deliberately do not count.** It attaches them
 *    with `excludeCompletion = true` (`:3115`), so even a screen full of video cannot
 *    complete. `sequence` does the same for its tab videos and gets its completion
 *    from reaching the last tab instead.
 *
 * ★★ WHAT "no" IN THAT COLUMN MEANS — AND WHAT IT DOES NOT MEAN.
 *
 * It means the author is not offered the three-state Progress gate control for that
 * type, because none of its three answers would be true. It does NOT mean the
 * `<blockingSection>` element is dropped. Those are different things in the Player,
 * and the difference is visible to the learner:
 *
 *   · `<blockingSection>false</blockingSection>` → the bar IS drawn (`components.js`
 *     `:5977` renders it for any non-empty value) and `_showLayoutsUntilNextLocked`
 *     immediately force-unlocks it (`:2923`), which swaps the text to
 *     `UI.completedContinue` — in this course, "Click the arrow down to continue
 *     learning" (`raw/Dynamo CE/content/en/UI.xml:149`). The following screens stay
 *     hidden until the learner clicks the arrow.
 *   · NO `<blockingSection>` at all → no bar, and `_showLayoutsUntilNextLocked`
 *     falls straight through, so every consecutive screen renders stacked in ONE
 *     scroll. The end-of-module footer also never appears, because it is revealed
 *     only when the last GATED layout unlocks.
 *
 * Omar, 2026-09-04, on the informational types: *"Their normal behaviour should
 * remain: 'Click the arrow down to continue learning'."* That sentence names the
 * FIRST bullet, so informational layouts resolve to `"false"`, not to absent.
 *
 * Measured on his own course before choosing, rather than reasoned about:
 * 115 of its 123 informational screens already carry `false`; the other 8 are `title`
 * screens with the element absent or empty. **Six of its 60 modules are made up
 * ENTIRELY of informational screens** — dropping the element would have left those six
 * with no continue bar and no end-of-module panel at all.
 *
 * This corrects an earlier version of this file, which resolved informational layouts
 * to `undefined` and would have done exactly that
 * (`feedback_do_not_remove_a_control_the_change_made_redundant`).
 *
 * ⚠️ THIS LIST IS PINNED-RUNTIME-SPECIFIC. It describes the Player as currently
 * pinned. Re-derive it when the runtime is re-pinned — a mechanical re-derivation
 * lives in the `bp-player-capability` harness rather than in a test, because the
 * Player is a sibling repository that CI does not check out.
 */
/** Layout types the Player can actually hold a learner on. */
export const BLOCKING_CAPABLE_LAYOUT_TYPES = [
    "fullscreen_video",
    "small_video",
    "sequence",
    "vertical_tabs",
    "horizontal_tabs",
    "icons_discover",
    "hidden_items",
    "quiz_images",
    "quiz_gaming",
];
/**
 * Layout types with no completion condition. Listed EXPLICITLY rather than derived as
 * "everything else", so that adding a 15th layout type to `LayoutContentSchema` fails
 * the completeness test instead of silently defaulting to one answer
 * (`feedback_guard_the_invariant_not_the_list`).
 */
export const INFORMATIONAL_LAYOUT_TYPES = [
    "title",
    "two_columns_text",
    "text_and_image",
    "fullscreen_text_and_image",
    "object_viewer",
];
const CAPABLE = new Set(BLOCKING_CAPABLE_LAYOUT_TYPES);
/** Can this layout type hold a learner until they have done something? */
export function layoutSupportsBlocking(type) {
    return CAPABLE.has(type);
}
/**
 * Resolve ONE layout's gate. The hierarchy is Omar's, 2026-09-04:
 *
 *   "Course-level 'Block progression' toggle = global/default state, and per-screen
 *    blocking control = override for that specific screen … Per-screen setting
 *    overrides the course-level setting whenever an explicit per-screen value has
 *    been chosen."
 *
 * with his rule about informational layouts applied FIRST, because it is about what
 * the Player can do rather than about what anyone chose:
 *
 *   1. the type cannot hold anyone   → `"false"`. The bar is drawn and is open from
 *                                      the moment the screen draws, which is the
 *                                      "Click the arrow down to continue learning"
 *                                      behaviour Omar asked to keep. `"true"` would
 *                                      be a promise the runtime cannot keep; ABSENT
 *                                      would remove the bar altogether — see the
 *                                      note above on why that is not "no gate".
 *   2. an explicit per-screen value  → that value, in either direction.
 *   3. otherwise                     → the course-level switch: `"true"` when it is
 *                                      on, `"false"` when it is off or unset.
 *
 * ★ CASE 3 IS THE ONLY MEANING "no stored state" HAS, and the editor now matches.
 * The control used to carry a third position, "No gate", which stored no `state` —
 * exactly what a screen nobody has touched stores, because `BlockingSectionStateSchema`
 * admits only `"true"` and `"false"`. One value on disk cannot mean two things, so a
 * button promising "no gate" produced a gate whenever the course switch was on.
 * Omar removed it: *"Please remove the No gate option completely."* (2026-09-04). The
 * control is now **Required** / **Optional**, with nothing selected until the author
 * chooses and a line saying what the screen does meanwhile.
 *
 * ⚠️ CONSEQUENCE, STATED BECAUSE IT IS A REAL ONE: there is no longer a way to put a
 * screen BACK to "follow the course" once it has been set, so the course switch stops
 * reaching that screen. Nothing regresses today — no gate-capable screen in the
 * imported BT course used the removed position, and no skeleton seeded it — and the
 * remedy if it is ever wanted is the same "reset every screen to follow the course
 * setting" action already offered for that course.
 */
export function resolveGate(layoutType, storedState, courseBlocksByDefault) {
    if (!layoutSupportsBlocking(layoutType))
        return "false";
    if (storedState === "true" || storedState === "false")
        return storedState;
    return courseBlocksByDefault ? "true" : "false";
}
//# sourceMappingURL=blocking.js.map